import { useEffect, useState } from 'react'
import AccessibleModal from './AccessibleModal'
import { useTranslation } from 'react-i18next'
import { Keyboard } from 'lucide-react'

/**
 * Listens for the `app:show-shortcuts` custom event and shows a help dialog.
 * Triggered by pressing `?` (Shift+/) in the Layout.
 */
export default function ShortcutsHelp() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const handler = () => setOpen(true)
    window.addEventListener('app:show-shortcuts', handler)
    return () => window.removeEventListener('app:show-shortcuts', handler)
  }, [])

  const shortcuts = [
    { keys: ['Ctrl', 'K'], desc: t('shortcuts.search') },
    { keys: ['Ctrl', 'Shift', 'N'], desc: t('shortcuts.newPost') },
    { keys: ['Ctrl', 'Shift', 'H'], desc: t('shortcuts.home') },
    { keys: ['/'], desc: t('shortcuts.focusSearch') },
    { keys: ['?'], desc: t('shortcuts.showHelp') },
    { keys: ['g', 'f'], desc: t('shortcuts.feed') },
    { keys: ['g', 'm'], desc: t('shortcuts.messages') },
    { keys: ['g', 'n'], desc: t('shortcuts.notifications') },
    { keys: ['g', 'h'], desc: t('shortcuts.home') },
    { keys: ['g', 's'], desc: t('shortcuts.settings') },
    { keys: ['Esc'], desc: t('shortcuts.closeModal') },
  ]

  return (
    <AccessibleModal
      open={open}
      onOpenChange={setOpen}
      title={t('shortcuts.title')}
      description={t('shortcuts.subtitle')}
      size="md"
    >
      <div className="flex items-center gap-2 mb-4 text-primary">
        <Keyboard className="w-4 h-4" />
        <span className="text-sm">{t('shortcuts.intro')}</span>
      </div>
      <ul className="space-y-2">
        {shortcuts.map((s, i) => (
          <li key={i} className="flex items-center justify-between gap-3 py-1.5 border-b border-border/50 last:border-0">
            <span className="text-sm">{s.desc}</span>
            <span className="flex items-center gap-1">
              {s.keys.map((k, ki) => (
                <span key={ki} className="flex items-center gap-1">
                  <kbd className="px-2 py-0.5 text-xs font-mono rounded bg-secondary border border-border text-foreground">
                    {k}
                  </kbd>
                  {ki < s.keys.length - 1 && <span className="text-muted-foreground text-xs">+</span>}
                </span>
              ))}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground mt-4">{t('shortcuts.footnote')}</p>
    </AccessibleModal>
  )
}
